import { useEffect, useRef } from 'react';

interface UseRandomIntervalOptions {
  enabled?: boolean;
}

// setTimeout (not setInterval/rAF) so the main thread idles between calls
export function useRandomInterval(
  minMs: number,
  maxMs: number,
  callback: () => void,
  { enabled = true }: UseRandomIntervalOptions = {}
): void {
  const savedCallback = useRef(callback);
  savedCallback.current = callback;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      const delay = minMs + Math.random() * (maxMs - minMs);
      timeoutId = setTimeout(() => {
        savedCallback.current();
        schedule();
      }, delay);
    };
    const stop = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = undefined;
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else if (!timeoutId) schedule();
    };

    if (!document.hidden) schedule();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [minMs, maxMs, enabled]);
}
